import * as cacheManager from "./lib/cacheManager";
import * as pageActions from "./actions/pageActions";
import * as sessionActions from "./actions/sessionActions";
import * as users from "./lib/api/users";
import App from "./components/templates/App";
import handleUnknownError from "./lib/handleUnknownError";
import history from "./lib/history";
import React from "react";
import ReactDOM from "react-dom";
import scriptjs from "scriptjs";
import store from "./lib/store";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { createRoot } from "react-dom/client";

window.__CLIENT_VERSION__ = __VERSION__;
if (__ERROR_REPORTING__) {
	import('@sentry/react')
    .then(Sentry => {
		Sentry.init({
			dsn: "https://6ca4c196469bb80913652ea86600130e@o132641.ingest.us.sentry.io/4508444741206017",
			environment: process.env.NODE_ENV,
			release: __VERSION__,
			integrations: [
			  Sentry.browserTracingIntegration(),
			  Sentry.replayIntegration(),
			],
			// Tracing
			tracesSampleRate: 1.0, //  Capture 100% of the transactions
			// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
			tracePropagationTargets: [/^\//],
			// Session Replay
			replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			ignoreErrors: [
				'Non-Error promise rejection captured', // seems to happen with outlook safe link validation, can probably switch sentry to only turn on once people are logged in instead to avoid this? https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/17
				'Object Not Found Matching Id' // this appears to be something caused by a chromium based scraping bots (but probably still outlook)  https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
			],
		  });
    });
}

window.addEventListener("unhandledrejection", e => {
	e.preventDefault();
	if (e.detail && e.detail.reason) {
		handleUnknownError(e.detail.reason, false);
	} else if (__DEV__) {
		console.error("Unhandled promise rejection", e.reason);
	}
});

cacheManager.initialize();
cacheManager.clearExpired().catch(err => handleUnknownError(err, false));

if (__DEV__) {
	console.info("Redux initialization complete");
	console.info(store.getState());
}

const siPromise = new Promise((resolve, reject) => {
	window.onSIApiReady = () => {
		resolve(window.SI_API);
	};
	scriptjs("//ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js", "jquery", () => {
		if (window.jQuery) {
			scriptjs(
				"//www.socialintents.com/api/socialintents.1.3.2.js#2c9fa5636e4317dc016e668448b91245",
				"social_intents",
				null,
				reject,
			);
		} else {
			reject(new Error("Failed to load jQuery"));
		}
	});
});
siPromise.catch(err => handleUnknownError(err, false));
store.dispatch(pageActions.pageSetSIPromise(siPromise));

// check with server to see if we are logged in before initial render
users
	.me()
	.then(currentUser => store.dispatch(sessionActions.setAuthenticated(true, currentUser)))
	.catch(err => {
		if (!err.response || err.response.status !== 401) {
			handleUnknownError(err, false);
		}

		// NOTE: Default redux isAuthenticated value is false, so no need to do anything here
	})
	.then(() => {
		const start = Date.now();
		const root = createRoot(document.getElementById("app"));
		root.render(
			<Provider store={store}>
				<Router history={history}>
					<App />
				</Router>
			</Provider>,
		);
		root.onComplete = () => {
			if (__DEV__) {
				const end = Date.now();
				console.info(`Initial render completed in ${end - start}ms`);
			}
		};
	});
